/* eslint-disable @typescript-eslint/consistent-type-definitions -- Zero mutator/query arg types must be `type` aliases to satisfy the ReadonlyJSONValue constraint; interfaces lack an implicit index signature. */
import { defineMutator } from "@rocicorp/zero"

import type { ZeroJwtPayload } from "@productlane/lib/src/browser"

import type { MutatorTx } from "../schema"
import type { JSONValue } from "@rocicorp/zero"
import {
	applyCreateProposal,
	applyDeleteProposal,
	applyEditProposal,
} from "./help-center-proposal-apply"
import { closeProposalAs } from "./help-center-proposal-status"

export type GroupChildArgs = {
	id: string
	kind: "EDIT" | "CREATE" | "DELETE"
	articleId?: string
	baseVersionId?: string
	proposedSteps?: JSONValue
	proposedTitle?: string
	proposedGroupId?: string
}

export type CreateGroupArgs = {
	id: string
	title?: string
	// 1-3 sentence agent-written rationale persisted on the group and shown
	// under the headline on the review card.
	description?: string
	source?: "MANUAL" | "CHANGELOG" | "ISSUE_COMPLETED"
	// Issues this agent run was triggered by — wired up via the M2M join table
	// `helpCenterProposalGroupToIssues`. Empty for MANUAL groups.
	issueIds?: string[]
	// `owner/repo#number` identifiers for the PRs the agent inspected. Stored
	// directly on the group as a string array (no FK target).
	pullRequestIdentifiers?: string[]
	authorType?: "USER" | "AGENT"
	children: GroupChildArgs[]
	submitForReview?: boolean
}

export type AcceptChildArgs = {
	proposalId: string
	// Required for EDIT children — client-generated ID for the new HelpCenterArticleVersion.
	newArticleVersionId?: string
	// Required for CREATE children.
	newArticleId?: string
	newArticleUrlName?: string
}

export type AcceptGroupArgs = {
	id: string
	childAcceptArgs: AcceptChildArgs[]
}

export type RejectGroupArgs = {
	id: string
	// Why the reviewer declined, so the docs agent can learn from it.
	reason?: string
	note?: string
}

export const helpCenterProposalGroupMutators = {
	createGroup: defineMutator(
		async ({
			tx,
			args,
			ctx: authData,
		}: {
			tx: MutatorTx
			args: CreateGroupArgs
			ctx: ZeroJwtPayload
		}) => {
			const now = Date.now()
			const status = args.submitForReview === false ? "DRAFT" : "OPEN"
			const authorType = args.authorType ?? "AGENT"

			await tx.mutate.helpCenterProposalGroups.insert({
				id: args.id,
				createdAt: now,
				updatedAt: now,
				version: 0,
				title: args.title,
				description: args.description,
				source: args.source ?? "MANUAL",
				authorType,
				createdByUserId: authorType === "USER" ? authData.userId : undefined,
				pullRequestIdentifiers: args.pullRequestIdentifiers ?? [],
				status,
				workspaceId: authData.workspaceId,
			})

			// Wire up the group ↔ issue M2M. Deduped here so a caller passing
			// the same id twice doesn't blow up the insert.
			const linkIssueIds = Array.from(new Set(args.issueIds ?? []))
			for (const issueId of linkIssueIds) {
				await tx.mutate.helpCenterProposalGroupToIssues.insert({
					A: args.id,
					B: issueId,
				})
			}

			for (const child of args.children) {
				await tx.mutate.helpCenterArticleProposals.insert({
					id: child.id,
					createdAt: now,
					updatedAt: now,
					version: 0,
					kind: child.kind,
					proposalGroupId: args.id,
					articleId: child.articleId,
					baseVersionId: child.baseVersionId,
					proposedSteps: child.proposedSteps ?? [],
					proposedTitle: child.proposedTitle,
					proposedGroupId: child.proposedGroupId,
					source: args.source ?? "MANUAL",
					authorType,
					createdByUserId: authorType === "USER" ? authData.userId : undefined,
					status,
					workspaceId: authData.workspaceId,
				})
			}
		},
	),

	// Best-effort group accept: each child runs independently. A child that
	// can't be applied is superseded individually; the rest still land. The
	// group's status is derived from its children at the end — ACCEPTED if
	// at least one child accepted, SUPERSEDED if every child failed.
	acceptGroup: defineMutator(
		async ({
			tx,
			args,
			ctx: authData,
		}: {
			tx: MutatorTx
			args: AcceptGroupArgs
			ctx: ZeroJwtPayload
		}) => {
			const now = Date.now()

			const [group] = await tx.query.helpCenterProposalGroups
				.where("id", "=", args.id)
				.run()
			if (!group || group.status !== "OPEN") {
				console.warn(
					`acceptGroup: group ${args.id} not found or not OPEN, skipping`,
				)
				return
			}

			const children = await tx.query.helpCenterArticleProposals
				.where("proposalGroupId", "=", args.id)
				.run()
			const acceptable = children.filter(
				(c) => c.status === "OPEN" || c.status === "DRAFT",
			)

			let acceptedCount = 0
			let supersededCount = 0

			const supersedeChild = async (childId: string) => {
				supersededCount++
				await tx.mutate.helpCenterArticleProposals.update({
					id: childId,
					...closeProposalAs({
						status: "SUPERSEDED",
						userId: authData.userId,
						now,
					}),
				})
			}

			const applyCtx = { workspaceId: authData.workspaceId, now }

			for (const child of acceptable) {
				const acceptArg = args.childAcceptArgs.find(
					(a) => a.proposalId === child.id,
				)

				let outcome: { kind: "accepted" } | { kind: "superseded" }
				let appliedVersionId: string | undefined

				if (child.kind === "DELETE") {
					outcome = await applyDeleteProposal(tx, child, applyCtx)
				} else if (child.kind === "CREATE") {
					if (!acceptArg?.newArticleId || !acceptArg.newArticleUrlName) {
						outcome = { kind: "superseded" }
					} else {
						outcome = await applyCreateProposal(
							tx,
							child,
							{
								newArticleId: acceptArg.newArticleId,
								newArticleUrlName: acceptArg.newArticleUrlName,
							},
							applyCtx,
						)
					}
				} else {
					// EDIT — `applyEditProposal` re-resolves the version chain so
					// siblings in this loop see the previous sibling's new HEAD.
					if (!acceptArg?.newArticleVersionId) {
						outcome = { kind: "superseded" }
					} else {
						outcome = await applyEditProposal(
							tx,
							child,
							{ newArticleVersionId: acceptArg.newArticleVersionId },
							applyCtx,
						)
						if (outcome.kind === "accepted") {
							appliedVersionId = acceptArg.newArticleVersionId
						}
					}
				}

				if (outcome.kind === "superseded") {
					await supersedeChild(child.id)
					continue
				}

				await tx.mutate.helpCenterArticleProposals.update({
					id: child.id,
					...closeProposalAs({
						status: "ACCEPTED",
						userId: authData.userId,
						now,
						appliedVersionId,
					}),
				})
				acceptedCount++
			}

			// Roll up the group's terminal status from per-child outcomes.
			await tx.mutate.helpCenterProposalGroups.update({
				id: args.id,
				...closeProposalAs({
					status:
						acceptedCount === 0 && supersededCount > 0
							? "SUPERSEDED"
							: "ACCEPTED",
					userId: authData.userId,
					now,
				}),
			})
		},
	),

	rejectGroup: defineMutator(
		async ({
			tx,
			args,
			ctx: authData,
		}: {
			tx: MutatorTx
			args: RejectGroupArgs
			ctx: ZeroJwtPayload
		}) => {
			const [group] = await tx.query.helpCenterProposalGroups
				.where("id", "=", args.id)
				.run()
			if (!group || group.status !== "OPEN") {
				console.warn(
					`rejectGroup: group ${args.id} not found or not OPEN, skipping`,
				)
				return
			}
			const now = Date.now()
			const reject = closeProposalAs({
				status: "REJECTED",
				userId: authData.userId,
				now,
			})
			await tx.mutate.helpCenterProposalGroups.update({
				id: args.id,
				...reject,
			})
			const children = await tx.query.helpCenterArticleProposals
				.where("proposalGroupId", "=", args.id)
				.run()
			for (const child of children) {
				if (child.status !== "OPEN" && child.status !== "DRAFT") continue
				await tx.mutate.helpCenterArticleProposals.update({
					id: child.id,
					...reject,
				})
			}
		},
	),
}
